import { type Ref, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { Button } from '@interxfi/design-system/components/button/button';
import type { ButtonState } from '@interxfi/design-system/components/button/shared';
import { DrawerButtonRow, DrawerContent } from '@interxfi/design-system/components/dialog/drawer/shared';
import { FormConfirmationDetails, type FormConfirmationGroup } from '../../../confirmation/form-confirmation';
import { useFormWizardContext, useFormWizardContextValue } from '../form-wizard-context';
import type { BaseFormWizardStepGeneric, FormWizardBaseStep } from './form-wizard-step';

export interface FormWizardReviewStep<
  TMappedData extends object,
  TContext extends object = {},
  TSteps extends BaseFormWizardStepGeneric = BaseFormWizardStepGeneric,
> extends FormWizardBaseStep {
  kind: 'review';
  backCta?: string;
  buildConfirmationDetails: (
    data: TMappedData,
    context: TContext | undefined,
    steps?: TSteps,
  ) => FormConfirmationGroup[] | Promise<FormConfirmationGroup[]>;
  saveCta: string;
}

interface FormWizardReviewStepProps<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>
  extends FormWizardReviewStep<TMappedData, TContext, TSteps> {
  goBack: VoidFunction;
  handleMapFormData: (data: TSteps, context: TContext | undefined) => TMappedData;
  onConfirm: (data: TMappedData) => void | Promise<void>;
  saveCtaRef: Ref<HTMLButtonElement> | undefined;
  saveCtaState: ButtonState | undefined;
}

export function FormWizardReviewStepComponent<TSteps extends BaseFormWizardStepGeneric, TMappedData extends object, TContext extends object = {}>({
  backCta,
  buildConfirmationDetails,
  handleMapFormData,
  goBack,
  onConfirm,
  saveCta,
  saveCtaRef,
  saveCtaState,
}: FormWizardReviewStepProps<TSteps, TMappedData, TContext>) {
  const [confirmationDetails, setConfirmationDetails] = useState<FormConfirmationGroup[]>([]);
  const context = useFormWizardContextValue<TSteps, TContext>();
  const fullFormData = useFormWizardContext<TSteps, undefined, TSteps>(useShallow((state) => state.steps));
  const mappedFormData = useMemo(() => handleMapFormData(fullFormData, context), [handleMapFormData, fullFormData, context]);
  const handleConfirm = useCallback(() => onConfirm(mappedFormData), [onConfirm, mappedFormData]);

  useEffect(() => {
    async function buildDetails() {
      setConfirmationDetails(await buildConfirmationDetails(mappedFormData, context, fullFormData));
    }

    void buildDetails();
  }, [buildConfirmationDetails, mappedFormData, context, fullFormData]);

  return (
    <DrawerContent>
      <FormConfirmationDetails groups={confirmationDetails} />

      <DrawerButtonRow>
        {backCta ? (
          <Button fullWidth onClick={goBack} size="large" type="button" variant="secondary">
            {backCta}
          </Button>
        ) : null}
        <Button fullWidth onClick={handleConfirm} ref={saveCtaRef} size="large" state={saveCtaState} type="button" variant="primary">
          {saveCta}
        </Button>
      </DrawerButtonRow>
    </DrawerContent>
  );
}
